import { useState, useRef, useEffect } from 'react';
import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Target, BarChart3, Brain, Users, TrendingUp, Shield, ChevronRight, Sparkles } from 'lucide-react';

const fadeUp = { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0 } };
const scaleIn = { hidden: { opacity: 0, scale: 0.9 }, visible: { opacity: 1, scale: 1 } };

function Section({ children, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.section ref={ref} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={fadeUp} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className={className}>
      {children}
    </motion.section>
  );
}

function Divider() {
  return <div className="max-w-xs mx-auto h-px bg-gradient-to-r from-transparent via-violet-500/20 to-transparent" />;
}

function AnimatedCounter({ target, suffix = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const count = useMotionValue(0);
  const rounded = useTransform(count, v => `${Math.round(v)}${suffix}`);

  useEffect(() => {
    if (inView) {
      animate(count, target, { duration: 2, ease: [0.22, 1, 0.36, 1] });
    }
  }, [inView, count, target]);

  return <motion.span ref={ref}>{rounded}</motion.span>;
}

/* ═══════════════════════════════════════════
   1. HERO
   ═══════════════════════════════════════════ */
function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="gj-hero">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/3 w-[700px] h-[700px] bg-violet-600/[0.06] rounded-full blur-[180px]" />
        <div className="absolute bottom-1/4 right-1/5 w-[500px] h-[500px] bg-pink-600/[0.04] rounded-full blur-[160px]" />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-16 w-full grid lg:grid-cols-[1fr_1.1fr] gap-8 lg:gap-6 items-center pt-32 pb-20">
        <motion.div initial={{ opacity: 0, x: -40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
          <span className="inline-block px-4 py-1.5 rounded-full border border-violet-500/30 bg-violet-500/5 text-violet-300 text-sm font-medium tracking-wider uppercase mb-6">
            Sistem Onboarding
          </span>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-semibold leading-[1.05] tracking-tight mb-6">
            {"\u0042\u00FC\u0079\u00FC\u006D\u0065\u0020\u0059\u006F\u006C\u0063\u0075\u006C\u0075\u011F\u0075\u006E\u0075"}
            <span className="block bg-gradient-to-r from-violet-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              {"Ba\u015Flat."}
            </span>
          </h1>
          <p className="text-xl text-zinc-400 max-w-lg mb-3 leading-relaxed">
            {"Bu bir hizmet de\u011Fil. Markan\u0131z i\u00E7in kurulan bir b\u00FCy\u00FCme sistemidir."}
          </p>
          <p className="text-base text-zinc-500 mb-10">
            {"Reklam ajans\u0131 de\u011Fil, performans orta\u011F\u0131y\u0131z."}
          </p>
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <Link to="/iletisim" className="group relative inline-flex items-center gap-2 px-8 py-4 rounded-full text-white font-semibold text-base tracking-wide overflow-hidden" style={{ background: 'linear-gradient(135deg, #5558E3 0%, #7C52D9 60%, #D43D85 100%)' }} data-testid="gj-hero-cta">
              <span className="absolute inset-0 bg-white/10 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 skew-x-[-20deg]" />
              <span className="relative z-10 flex items-center gap-2">
                {"Marka Ke\u015Fif Toplant\u0131s\u0131 Planla"}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
            <span className="text-sm text-zinc-500 mt-2 sm:mt-3">{"30 dakikal\u0131k \u00FCcretsiz stratejik analiz"}</span>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.2, delay: 0.2 }} className="hidden lg:flex items-center justify-center">
          <SystemNetwork />
        </motion.div>
      </div>
    </section>
  );
}

/* Premium network visualization */
function SystemNetwork() {
  const nodes = [
    { id: 'strateji', label: 'Strateji', x: 50, y: 12, icon: '01' },
    { id: 'veri', label: 'Veri', x: 82, y: 35, icon: '02' },
    { id: 'analiz', label: 'Analiz', x: 15, y: 32, icon: '03' },
    { id: 'ai', label: 'AI', x: 22, y: 62, icon: '04' },
    { id: 'otomasyon', label: 'Otomasyon', x: 72, y: 72, icon: '05' },
    { id: 'olcek', label: "\u00D6l\u00E7ek", x: 88, y: 55, icon: '06' },
  ];
  const center = { x: 50, y: 45 };
  const connections = [
    [0,1],[0,2],[1,5],[2,3],[3,4],[4,5],[1,4],[2,4],[0,3],
  ];

  return (
    <div className="relative w-full max-w-[580px] aspect-square">
      {/* ambient glow layers */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] h-[70%] bg-violet-500/[0.04] rounded-full blur-[80px]" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40%] h-[40%] bg-purple-500/[0.08] rounded-full blur-[60px]" />

      {/* orbiting rings */}
      <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[55%] h-[55%] rounded-full border border-violet-500/[0.06]" animate={{ rotate: 360 }} transition={{ duration: 60, repeat: Infinity, ease: 'linear' }} />
      <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] rounded-full border border-violet-500/[0.04]" animate={{ rotate: -360 }} transition={{ duration: 90, repeat: Infinity, ease: 'linear' }} />
      <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[95%] h-[95%] rounded-full border border-zinc-800/30" />

      {/* SVG connections */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="connGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#7C52D9" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#D43D85" stopOpacity="0.2" />
          </linearGradient>
          <radialGradient id="centerGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0" />
          </radialGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="0.5" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {/* center glow */}
        <circle cx={center.x} cy={center.y} r="8" fill="url(#centerGlow)" />

        {/* node-to-center lines */}
        {nodes.map((n, i) => (
          <motion.line key={`c-${i}`} x1={center.x} y1={center.y} x2={n.x} y2={n.y} stroke="url(#connGrad)" strokeWidth="0.3" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.5, delay: 0.5 + i * 0.15, ease: 'easeOut' }} />
        ))}

        {/* inter-node lines */}
        {connections.map(([a, b], i) => (
          <motion.line key={`l-${i}`} x1={nodes[a].x} y1={nodes[a].y} x2={nodes[b].x} y2={nodes[b].y} stroke="url(#connGrad)" strokeWidth="0.15" strokeDasharray="0.5 1" initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 0.5 }} transition={{ duration: 2, delay: 1 + i * 0.1, ease: 'easeOut' }} />
        ))}

        {/* center dot */}
        <motion.circle cx={center.x} cy={center.y} r="1.8" fill="#8B5CF6" filter="url(#glow)" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ duration: 0.6, delay: 0.3 }} />
        <motion.circle cx={center.x} cy={center.y} r="4" fill="none" stroke="#8B5CF6" strokeWidth="0.3" animate={{ r: [4, 7, 4], opacity: [0.4, 0, 0.4] }} transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }} />
        <motion.circle cx={center.x} cy={center.y} r="5" fill="none" stroke="#8B5CF6" strokeWidth="0.15" animate={{ r: [5, 10, 5], opacity: [0.2, 0, 0.2] }} transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }} />

        {/* data flow particles along center connections */}
        {nodes.map((n, i) => (
          <motion.circle key={`p-${i}`} r="0.4" fill="#A78BFA"
            animate={{
              cx: [center.x, n.x, center.x],
              cy: [center.y, n.y, center.y],
              opacity: [0, 0.8, 0],
            }}
            transition={{ duration: 3 + i * 0.5, repeat: Infinity, delay: i * 0.8, ease: 'easeInOut' }}
          />
        ))}
      </svg>

      {/* center label */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center z-10">
        <motion.div
          className="w-20 h-20 rounded-full bg-violet-500/10 backdrop-blur-sm border border-violet-500/20 flex items-center justify-center"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="w-3.5 h-3.5 rounded-full bg-violet-400 shadow-[0_0_24px_rgba(139,92,246,0.7)]" />
        </motion.div>
        <motion.span
          className="text-base font-semibold text-violet-300 mt-3 tracking-wide"
         
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 1 }}
        >
          {"\u0042\u00FC\u0079\u00FC\u006D\u0065"}
        </motion.span>
      </div>

      {/* outer nodes */}
      {nodes.map((n, i) => (
        <motion.div
          key={n.id}
          className="absolute z-10 flex flex-col items-center"
          style={{ left: `${n.x}%`, top: `${n.y}%`, transform: 'translate(-50%, -50%)' }}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.8 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="w-12 h-12 rounded-xl bg-zinc-900/80 backdrop-blur-sm border border-zinc-700/40 flex items-center justify-center hover:border-violet-500/30 transition-colors duration-500 group cursor-default">
            <span className="text-sm font-mono text-violet-400/60 group-hover:text-violet-300 transition-colors">{n.icon}</span>
          </div>
          <span className="text-base text-zinc-400 mt-2 whitespace-nowrap font-medium">{n.label}</span>
        </motion.div>
      ))}

      {/* floating ambient particles */}
      {[...Array(8)].map((_, i) => (
        <motion.div
          key={`fp-${i}`}
          className="absolute w-1 h-1 rounded-full bg-violet-400/20"
          style={{ left: `${15 + Math.random() * 70}%`, top: `${15 + Math.random() * 70}%` }}
          animate={{
            y: [0, -15 - Math.random() * 20, 0],
            x: [0, (Math.random() - 0.5) * 20, 0],
            opacity: [0, 0.6, 0],
          }}
          transition={{ duration: 4 + Math.random() * 3, repeat: Infinity, delay: Math.random() * 5, ease: 'easeInOut' }}
        />
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════
   2. REALITY BREAK
   ═══════════════════════════════════════════ */
function RealityBreak() {
  const cards = [
    { icon: <Zap className="w-5 h-5" />, title: "Reklam var, sistem yok", desc: "B\u00FCt\u00E7e harcan\u0131r ama d\u00F6n\u00FC\u015F\u00FCm sistematik de\u011Fil." },
    { icon: <TrendingUp className="w-5 h-5" />, title: "Trafik var, d\u00F6n\u00FC\u015F\u00FCm yok", desc: "Ziyaret\u00E7i gelir ama m\u00FC\u015Fteri olmaz." },
    { icon: <Target className="w-5 h-5" />, title: "\u00DCr\u00FCn var, konumland\u0131rma yok", desc: "Kaliteli \u00FCr\u00FCn g\u00F6r\u00FClmez, duyulmaz." },
    { icon: <BarChart3 className="w-5 h-5" />, title: "Veri var, yorumlayan yok", desc: "Dashboard dolu ama aksiyon al\u0131nmaz." },
  ];

  return (
    <Section className="py-28 lg:py-40 px-6 lg:px-12" data-testid="gj-reality">
      <div className="max-w-6xl mx-auto text-center">
        <h2 className="text-4xl sm:text-5xl font-semibold mb-16 tracking-tight">
          {"Neden \u00E7o\u011Fu marka "}<span className="text-zinc-500">{"b\u00FCy\u00FCyemez?"}</span>
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {cards.map((c, i) => (
            <motion.div key={i} variants={scaleIn} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-40px' }} transition={{ duration: 0.5, delay: i * 0.1 }} className="group relative p-7 rounded-2xl border border-zinc-800/60 bg-zinc-900/40 backdrop-blur-sm hover:border-violet-500/30 transition-all duration-500 hover:-translate-y-1">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-b from-violet-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center text-violet-400 mb-5">{c.icon}</div>
                <h3 className="text-white font-semibold text-xl mb-3">{c.title}</h3>
                <p className="text-zinc-400 text-base leading-relaxed">{c.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
        <motion.p className="mt-16 text-xl sm:text-2xl font-semibold text-white/90 tracking-tight" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.4 }}>
          {"Sorun pazarlama de\u011Fil. "}<span className="bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">{"Sistem eksikli\u011Fi."}</span>
        </motion.p>
      </div>
    </Section>
  );
}

/* ═══════════════════════════════════════════
   3. PROCESS TIMELINE — enhanced
   ═══════════════════════════════════════════ */
function ProcessTimeline() {
  const steps = [
    { num: '01', title: "\u00D6n G\u00F6r\u00FC\u015Fme", desc: "Marka, hedef ve darbo\u011Faz analizi. Nerede oldu\u011Funuzu ve nereye gitmek istedi\u011Finizi birlikte belirleriz.", icon: <Users className="w-5 h-5" /> },
    { num: '02', title: 'Derin Analiz', desc: "Rakip, m\u00FC\u015Fteri ve veri analizi. Pazardaki f\u0131rsatlar\u0131 ve tehditleri sistematik olarak ortaya koyar\u0131z.", icon: <BarChart3 className="w-5 h-5" /> },
    { num: '03', title: "Strateji Tasar\u0131m\u0131", desc: "Konumland\u0131rma, funnel ve b\u00FCy\u00FCme plan\u0131. Markan\u0131za \u00F6zel b\u00FCy\u00FCme haritas\u0131 olu\u015Fturulur.", icon: <Target className="w-5 h-5" /> },
    { num: '04', title: 'Sistem Kurulumu', desc: "Web, reklam, AI ve otomasyon. T\u00FCm par\u00E7alar entegre bir sistem olarak hayata ge\u00E7er.", icon: <Zap className="w-5 h-5" /> },
    { num: '05', title: "\u00D6l\u00E7ekleme", desc: "Test, optimize, b\u00FCy\u00FCt. Veriyle y\u00F6nlendirilen s\u00FCrekli iyile\u015Ftirme d\u00F6ng\u00FCs\u00FC ba\u015Flar.", icon: <TrendingUp className="w-5 h-5" /> },
  ];

  return (
    <Section className="py-28 lg:py-40 px-6 lg:px-12 relative" data-testid="gj-process">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-0 w-[400px] h-[400px] bg-violet-600/[0.03] rounded-full blur-[120px]" />
      </div>
      <div className="max-w-5xl mx-auto relative z-10">
        <div className="text-center mb-20">
          <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight">
            {"B\u00FCy\u00FCme Sistemi "}<span className="text-zinc-500">{"Nas\u0131l Kurulur?"}</span>
          </h2>
        </div>

        <div className="relative">
          {/* progress line */}
          <div className="absolute left-6 lg:left-1/2 top-0 bottom-0 w-px bg-zinc-800/60 -translate-x-1/2" />
          <motion.div className="absolute left-6 lg:left-1/2 top-0 w-px bg-gradient-to-b from-violet-500 via-purple-500 to-pink-500 -translate-x-1/2 origin-top" style={{ bottom: 0 }} initial={{ scaleY: 0 }} whileInView={{ scaleY: 1 }} viewport={{ once: true }} transition={{ duration: 2, ease: [0.22, 1, 0.36, 1] }} />

          {steps.map((s, i) => {
            const isLeft = i % 2 === 0;
            return (
              <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.6, delay: i * 0.15 }} className={`relative flex items-start gap-6 mb-20 last:mb-0 ${isLeft ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                <div className={`flex-1 pl-16 lg:pl-0 ${isLeft ? 'lg:text-right lg:pr-16' : 'lg:text-left lg:pl-16'}`}>
                  <span className="text-violet-400/50 text-sm font-mono tracking-widest">{s.num}</span>
                  <h3 className="text-2xl font-semibold text-white mt-1 mb-3">{s.title}</h3>
                  <p className="text-zinc-400 text-base leading-relaxed max-w-sm" style={{ marginLeft: isLeft ? 'auto' : undefined }}>{s.desc}</p>
                </div>

                {/* node */}
                <div className="absolute left-6 lg:left-1/2 -translate-x-1/2 z-10">
                  <motion.div
                    className="w-12 h-12 rounded-full bg-zinc-900 border-2 border-violet-500/30 flex items-center justify-center text-violet-400 shadow-[0_0_20px_rgba(139,92,246,0.15)]"
                    whileInView={{ borderColor: 'rgba(139,92,246,0.6)', boxShadow: '0 0 30px rgba(139,92,246,0.25)' }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.15 + 0.3 }}
                  >
                    {s.icon}
                  </motion.div>
                </div>

                <div className="flex-1 hidden lg:block" />
              </motion.div>
            );
          })}
        </div>
      </div>
    </Section>
  );
}

/* ═══════════════════════════════════════════
   4. AIFABRIC
   ═══════════════════════════════════════════ */
function AifabricSystem() {
  const blocks = [
    { icon: <Brain className="w-5 h-5" />, title: "AI destekli sat\u0131\u015F sistemleri", desc: "Yapay zeka ile m\u00FC\u015Fteri davran\u0131\u015Flar\u0131n\u0131 analiz eder, sat\u0131\u015F f\u0131rsatlar\u0131n\u0131 otomatik yakalar." },
    { icon: <BarChart3 className="w-5 h-5" />, title: "Davran\u0131\u015F analizi", desc: "Kullan\u0131c\u0131 yolculu\u011Funu izler, darbo\u011Fazlar\u0131 tespit eder, \u00E7\u00F6z\u00FCm \u00FCretir." },
    { icon: <Zap className="w-5 h-5" />, title: "Otomatik d\u00F6n\u00FC\u015F\u00FCm optimizasyonu", desc: "A/B testler, funnel optimizasyonu ve dinamik i\u00E7erik ile d\u00F6n\u00FC\u015F\u00FCm oranlar\u0131n\u0131 art\u0131r\u0131r." },
    { icon: <Sparkles className="w-5 h-5" />, title: "\u0130\u00E7erik ve reklam zekas\u0131", desc: "AI ile kitleye \u00F6zel i\u00E7erik \u00FCretimi ve reklam performans optimizasyonu." },
  ];

  return (
    <Section className="py-28 lg:py-40 px-6 lg:px-12 relative" data-testid="gj-aifabric">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-purple-600/[0.04] rounded-full blur-[140px]" />
      </div>
      <div className="max-w-6xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <span className="inline-block px-3 py-1 rounded-full border border-violet-500/20 bg-violet-500/5 text-violet-300 text-xs font-medium tracking-wider uppercase mb-4">aifabric</span>
          <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight">
            {"Sadece \u0130nsan De\u011Fil, "}<span className="text-zinc-500">{"Sistem \u00C7al\u0131\u015F\u0131r"}</span>
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-5 mb-14">
          {blocks.map((b, i) => (
            <motion.div key={i} variants={scaleIn} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }} className="group p-7 rounded-2xl border border-zinc-800/60 bg-zinc-900/30 backdrop-blur-sm hover:border-violet-500/20 hover:-translate-y-1 transition-all duration-500">
              <div className="w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center text-violet-400 mb-5 group-hover:bg-violet-500/15 transition-colors">{b.icon}</div>
              <h3 className="text-white font-semibold text-xl mb-3">{b.title}</h3>
              <p className="text-zinc-400 text-base leading-relaxed">{b.desc}</p>
            </motion.div>
          ))}
        </div>
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.3 }} className="text-center p-10 rounded-2xl border border-violet-500/10 bg-gradient-to-br from-violet-500/[0.04] to-transparent">
          <p className="text-xl sm:text-2xl font-semibold text-white/90">
            {"\"Markan\u0131z sadece g\u00F6r\u00FClmez. "}<span className="bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">{"\u00D6\u011Frenir. Geli\u015Fir. Sat\u0131\u015F yapar.\""}</span>
          </p>
        </motion.div>
      </div>
    </Section>
  );
}

/* ═══════════════════════════════════════════
   5. MENTORSHIP — enhanced with photo area
   ═══════════════════════════════════════════ */
function MentorshipSection() {
  const points = [
    { title: "Birebir ment\u00F6rl\u00FCk", desc: "Stratejik kararlarda yan\u0131n\u0131zday\u0131z. Her ad\u0131mda rehberlik." },
    { title: "Stratejik karar deste\u011Fi", desc: "Veri odakl\u0131 kararlar\u0131n arkas\u0131ndaki zihin g\u00FCc\u00FCn\u00FC sa\u011Fl\u0131yoruz." },
    { title: "S\u00FCrekli y\u00F6nlendirme", desc: "Pazar de\u011Fi\u015Ftik\u00E7e strateji de de\u011Fi\u015Fir. Biz bu d\u00F6ng\u00FCn\u00FCn i\u00E7indeyiz." },
  ];

  return (
    <Section className="py-28 lg:py-40 px-6 lg:px-12" data-testid="gj-mentor">
      <div className="max-w-5xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight mb-8">
              {"Bir ajans de\u011Fil,"}<br /><span className="text-zinc-500">{"zihin ortakl\u0131\u011F\u0131"}</span>
            </h2>
            <div className="space-y-7">
              {points.map((p, i) => (
                <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.15 }} className="flex items-start gap-4 group">
                  <div className="w-9 h-9 rounded-lg bg-violet-500/10 flex items-center justify-center text-violet-400 flex-shrink-0 mt-0.5 group-hover:bg-violet-500/20 transition-colors">
                    <ChevronRight className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg mb-1">{p.title}</h3>
                    <p className="text-zinc-400 text-base leading-relaxed">{p.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <motion.div variants={scaleIn} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.2 }} className="relative">
            {/* photo card with glassmorphism */}
            <div className="relative p-10 rounded-3xl border border-zinc-800/60 bg-zinc-900/30 backdrop-blur-sm overflow-hidden">
              {/* decorative bg */}
              <div className="absolute top-0 right-0 w-40 h-40 bg-violet-500/[0.06] rounded-full blur-[60px]" />
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-pink-500/[0.04] rounded-full blur-[50px]" />

              <div className="relative text-center">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-violet-500/20 to-pink-500/10 mx-auto mb-6 flex items-center justify-center border border-violet-500/20">
                  <span className="text-3xl font-bold text-violet-300">TT</span>
                </div>
                <p className="text-base text-violet-300/70 font-medium tracking-wider uppercase mb-6">Taner Torun</p>
                <div className="w-12 h-px bg-gradient-to-r from-transparent via-violet-500/30 to-transparent mx-auto mb-6" />
                <p className="text-2xl font-semibold text-white/90 mb-3">{"\"Bu bir hizmet de\u011Fil."}</p>
                <p className="text-2xl font-semibold bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">{"Bir zihin ortakl\u0131\u011F\u0131d\u0131r.\""}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </Section>
  );
}

/* ═══════════════════════════════════════════
   6. GROWTH ROADMAP
   ═══════════════════════════════════════════ */
function GrowthRoadmap() {
  const phases = [
    { period: '3 Ay', title: 'Temel Sistem', desc: "Altyap\u0131 kurulur, ilk sonu\u00E7lar al\u0131n\u0131r. Veri toplama ve analiz ba\u015Flang\u0131c\u0131.", color: 'from-violet-500/20 to-violet-500/5', accent: 'border-violet-500/20' },
    { period: '6 Ay', title: 'Optimizasyon', desc: "Stabil b\u00FCy\u00FCme sa\u011Flan\u0131r. Funnel optimize edilir, d\u00F6n\u00FC\u015F\u00FCm oranlar\u0131 y\u00FCkseltilir.", color: 'from-purple-500/20 to-purple-500/5', accent: 'border-purple-500/20' },
    { period: '12 Ay', title: 'Dominasyon', desc: "\u00D6l\u00E7ekleme ve otomasyonla s\u00FCrd\u00FCr\u00FClebilir b\u00FCy\u00FCme. Pazar hakimiyeti.", color: 'from-pink-500/20 to-pink-500/5', accent: 'border-pink-500/20' },
  ];

  return (
    <Section className="py-28 lg:py-40 px-6 lg:px-12" data-testid="gj-roadmap">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight">
            {"Planl\u0131 "}<span className="text-zinc-500">{"b\u00FCy\u00FCme"}</span>
          </h2>
        </div>
        <div className="grid lg:grid-cols-3 gap-6">
          {phases.map((p, i) => (
            <motion.div key={i} variants={scaleIn} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.15 }} className="relative group">
              <div className={`absolute inset-0 rounded-2xl bg-gradient-to-b ${p.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
              <div className={`relative p-9 rounded-2xl border border-zinc-800/60 bg-zinc-900/30 backdrop-blur-sm h-full group-hover:${p.accent} transition-colors duration-500`}>
                <span className="text-4xl font-bold bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">{p.period}</span>
                <h3 className="text-white font-semibold text-xl mt-4 mb-3">{p.title}</h3>
                <p className="text-zinc-400 text-base leading-relaxed">{p.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ═══════════════════════════════════════════
   7. SOCIAL PROOF — animated counters
   ═══════════════════════════════════════════ */
function SocialProof() {
  const stats = [
    { value: 240, suffix: '%', label: "Ortalama d\u00F6n\u00FC\u015F\u00FCm art\u0131\u015F\u0131" },
    { value: 46, suffix: '+', label: 'Tamamlanan proje' },
    { value: 180, suffix: '%', label: "Organik trafik b\u00FCy\u00FCmesi" },
    { value: 20, suffix: "+", label: "Y\u0131ll\u0131k deneyim" },
  ];

  return (
    <Section className="py-28 lg:py-40 px-6 lg:px-12 relative" data-testid="gj-proof">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/3 w-[400px] h-[400px] bg-violet-600/[0.04] rounded-full blur-[140px]" />
      </div>
      <div className="max-w-6xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight">
            Rakamlar <span className="text-zinc-500">{"konu\u015Fur"}</span>
          </h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((s, i) => (
            <motion.div key={i} variants={scaleIn} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }} className="text-center p-7 rounded-2xl border border-zinc-800/40 bg-zinc-900/20 hover:border-zinc-700/50 transition-colors">
              <p className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent mb-3">
                <AnimatedCounter target={s.value} suffix={s.suffix} />
              </p>
              <p className="text-zinc-400 text-base">{s.label}</p>
            </motion.div>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-5 mt-12">
          {[
            { brand: "E-ticaret Markas\u0131", result: "CVR %3.2 \u2192 %7.8", detail: "Funnel optimizasyonu + AI tabanl\u0131 reklam y\u00F6netimi" },
            { brand: "SaaS Giri\u015Fimi", result: "MRR 3x b\u00FCy\u00FCme", detail: "Konumland\u0131rma + b\u00FCy\u00FCme sistemi kurulumu" },
          ].map((c, i) => (
            <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.3 + i * 0.15 }} className="p-7 rounded-2xl border border-zinc-800/40 bg-zinc-900/20 hover:border-zinc-700/40 transition-colors">
              <p className="text-sm text-violet-400/70 uppercase tracking-wider mb-2">{c.brand}</p>
              <p className="text-2xl font-semibold text-white mb-2">{c.result}</p>
              <p className="text-zinc-400 text-base">{c.detail}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ═══════════════════════════════════════════
   8. ELITE FILTER
   ═══════════════════════════════════════════ */
function EliteFilter() {
  const criteria = [
    { icon: <TrendingUp className="w-5 h-5" />, text: "B\u00FCy\u00FCmeye haz\u0131r olanlar", desc: "Status quo ile yetinmeyen, \u00F6l\u00E7eklenmeyi hedefleyen markalar." },
    { icon: <Shield className="w-5 h-5" />, text: "Yat\u0131r\u0131m yapabilenler", desc: "B\u00FCy\u00FCmenin yat\u0131r\u0131m gerektirdi\u011Fini bilen ve buna haz\u0131r i\u015Fletmeler." },
    { icon: <Zap className="w-5 h-5" />, text: "Sistem kurmak isteyenler", desc: "Tek seferlik kampanya de\u011Fil, s\u00FCrd\u00FCr\u00FClebilir b\u00FCy\u00FCme sistemi arayanlar." },
  ];

  return (
    <Section className="py-28 lg:py-40 px-6 lg:px-12" data-testid="gj-elite">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight mb-4">
          {"Her markayla "}<span className="text-zinc-500">{"\u00E7al\u0131\u015Fm\u0131yoruz"}</span>
        </h2>
        <p className="text-zinc-500 text-base mb-14">{"S\u0131n\u0131rl\u0131 kontenjan, maksimum odaklanma."}</p>
        <div className="grid sm:grid-cols-3 gap-5">
          {criteria.map((c, i) => (
            <motion.div key={i} variants={scaleIn} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.12 }} className="p-8 rounded-2xl border border-zinc-800/60 bg-zinc-900/30 text-center hover:border-violet-500/20 hover:-translate-y-1 transition-all duration-500">
              <div className="w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center text-violet-400 mx-auto mb-5">{c.icon}</div>
              <h3 className="text-white font-semibold text-xl mb-3">{c.text}</h3>
              <p className="text-zinc-400 text-base leading-relaxed">{c.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ═══════════════════════════════════════════
   9. MINI SIMULATION
   ═══════════════════════════════════════════ */
function MiniSimulation() {
  const [ciro, setCiro] = useState(100000);
  const [trafik, setTrafik] = useState(5000);
  const [sektor, setSektor] = useState('eticaret');

  const multipliers = { eticaret: 2.4, saas: 3.1, hizmet: 2.0, saglik: 2.2 };
  const sektorLabels = { eticaret: 'E-ticaret', saas: 'SaaS', hizmet: 'Hizmet', saglik: "Sa\u011Fl\u0131k" };
  const mult = multipliers[sektor] || 2.4;
  const potansiyel = Math.round(ciro * mult);
  const trafikPotansiyel = Math.round(trafik * 1.8);

  return (
    <Section className="py-28 lg:py-40 px-6 lg:px-12 relative" data-testid="gj-simulation">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-1/4 left-1/4 w-[500px] h-[500px] bg-violet-600/[0.04] rounded-full blur-[140px]" />
      </div>
      <div className="max-w-4xl mx-auto relative z-10">
        <div className="text-center mb-14">
          <span className="inline-block px-3 py-1 rounded-full border border-violet-500/20 bg-violet-500/5 text-violet-300 text-sm font-medium tracking-wider uppercase mb-4">{"Interaktif"}</span>
          <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight">
            {"Potansiyel b\u00FCy\u00FCme "}<span className="text-zinc-500">{"sim\u00FClasyonu"}</span>
          </h2>
        </div>
        <div className="p-8 sm:p-10 rounded-3xl border border-zinc-800/60 bg-zinc-900/30 backdrop-blur-sm">
          <div className="grid sm:grid-cols-3 gap-8 mb-10">
            <div>
              <label className="text-sm text-zinc-500 block mb-3">{"Ayl\u0131k Ciro Hedefi"}</label>
              <div className="text-white font-semibold text-xl mb-3">{ciro.toLocaleString('tr-TR')} TL</div>
              <input type="range" min="50000" max="2000000" step="50000" value={ciro} onChange={(e) => setCiro(Number(e.target.value))} className="w-full accent-violet-500 h-1.5 bg-zinc-800 rounded-full appearance-none cursor-pointer" data-testid="sim-ciro-slider" />
            </div>
            <div>
              <label className="text-sm text-zinc-500 block mb-3">{"Mevcut Ayl\u0131k Trafik"}</label>
              <div className="text-white font-semibold text-xl mb-3">{trafik.toLocaleString('tr-TR')}</div>
              <input type="range" min="500" max="100000" step="500" value={trafik} onChange={(e) => setTrafik(Number(e.target.value))} className="w-full accent-violet-500 h-1.5 bg-zinc-800 rounded-full appearance-none cursor-pointer" data-testid="sim-trafik-slider" />
            </div>
            <div>
              <label className="text-xs text-zinc-500 block mb-3">{"Sekt\u00F6r"}</label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(sektorLabels).map(([key, label]) => (
                  <button key={key} onClick={() => setSektor(key)} className={`px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 ${sektor === key ? 'bg-violet-500/20 border-violet-500/40 text-violet-300' : 'bg-zinc-800/50 border-zinc-700/50 text-zinc-500 hover:text-zinc-300'} border`} data-testid={`sim-sektor-${key}`}>{label}</button>
                ))}
              </div>
            </div>
          </div>
          <div className="pt-8 border-t border-zinc-800/60">
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="p-6 rounded-xl bg-zinc-800/30 border border-zinc-700/30">
                <p className="text-sm text-zinc-500 mb-2">{"Potansiyel Ciro (12 Ay)"}</p>
                <p className="text-3xl font-bold bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">{potansiyel.toLocaleString('tr-TR')} TL</p>
                <p className="text-sm text-emerald-400/70 mt-2">+{Math.round((mult - 1) * 100)}% {"b\u00FCy\u00FCme potansiyeli"}</p>
              </div>
              <div className="p-6 rounded-xl bg-zinc-800/30 border border-zinc-700/30">
                <p className="text-sm text-zinc-500 mb-2">{"Hedef Trafik (12 Ay)"}</p>
                <p className="text-3xl font-bold bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">{trafikPotansiyel.toLocaleString('tr-TR')}</p>
                <p className="text-sm text-emerald-400/70 mt-2">{"+ %80 trafik art\u0131\u015F\u0131"}</p>
              </div>
            </div>
            <p className="text-xs text-zinc-600 mt-5 text-center">{"*Sekt\u00F6r ortalamalar\u0131na dayal\u0131 projeksiyon. Ger\u00E7ek sonu\u00E7lar de\u011Fi\u015Fkenlik g\u00F6sterebilir."}</p>
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ═══════════════════════════════════════════
   10. FINAL CTA
   ═══════════════════════════════════════════ */
function FinalCTA() {
  return (
    <section className="py-32 lg:py-44 px-6 lg:px-12 relative" data-testid="gj-final-cta">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-violet-600/[0.06] rounded-full blur-[180px]" />
      </div>
      <motion.div className="max-w-3xl mx-auto text-center relative z-10" initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
        <h2 className="text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight mb-6">
          {"Haz\u0131rsan\u0131z"}<br />
          <span className="bg-gradient-to-r from-violet-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">{"ba\u015Flayal\u0131m."}</span>
        </h2>
        <p className="text-zinc-400 text-xl mb-12 max-w-md mx-auto">{"Bu bir teklif de\u011Fil. Bir ba\u015Flang\u0131\u00E7."}</p>
        <Link to="/iletisim" className="group relative inline-flex items-center gap-3 px-12 py-5 rounded-full text-white font-semibold text-base tracking-wide overflow-hidden" style={{ background: 'linear-gradient(135deg, #5558E3 0%, #7C52D9 60%, #D43D85 100%)' }} data-testid="gj-final-cta-btn">
          <span className="absolute inset-0 bg-white/10 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 skew-x-[-20deg]" />
          <span className="relative z-10 flex items-center gap-3">
            {"Marka Ke\u015Fif Toplant\u0131s\u0131 Planla"}
            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
          </span>
        </Link>
        <p className="text-sm text-zinc-600 mt-5">{"S\u0131n\u0131rl\u0131 kontenjan"}</p>
      </motion.div>
    </section>
  );
}

/* ═══════════════════════════════════════════
   PAGE
   ═══════════════════════════════════════════ */
export default function GrowthJourneyPage() {
  return (
    <div className="bg-[#070707] text-white min-h-screen" data-testid="growth-journey-page">
      <HeroSection />
      <Divider />
      <RealityBreak />
      <Divider />
      <ProcessTimeline />
      <Divider />
      <AifabricSystem />
      <Divider />
      <MentorshipSection />
      <Divider />
      <GrowthRoadmap />
      <Divider />
      <SocialProof />
      <Divider />
      <EliteFilter />
      <Divider />
      <MiniSimulation />
      <Divider />
      <FinalCTA />
    </div>
  );
}
